"use client";

import { usePathname } from "@/i18n/routing";

/**
 * Client-side header/footer switch for the booking funnel.
 *
 * The booking flow (`/angebot`, except the success page) replaces the full
 * navbar with a slim top bar and hides the global footer. This decision MUST
 * be client-side: the `[locale]` layout is shared across all pages and does
 * NOT re-render on client navigation, so deciding via the request's
 * `x-pathname` left the chrome stale — navigating Home → /angebot via a link
 * kept the full header + footer (and dropped the slim bar) until a hard
 * reload. `usePathname()` updates on every navigation, so the switch is
 * always correct.
 *
 * The two headers and the footer are rendered on the server and passed in as
 * props (Server Components as children of a Client Component), so no Sanity
 * data fetching moves to the client.
 */
export function SiteChrome({
  siteHeader,
  bookingTopBar,
  footer,
  children,
}: {
  siteHeader: React.ReactNode;
  bookingTopBar: React.ReactNode;
  footer: React.ReactNode;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  // Ads-Landingpage /preisrechner (25.09.2026): gleicher schlanker Kopf ohne
  // Navigation und ohne Footer-Menü wie der Kalkulator — kein Absprung.
  const isBookingFlow =
    (pathname.startsWith("/angebot") && !pathname.startsWith("/angebot/erfolg")) ||
    pathname.startsWith("/preisrechner");

  return (
    <>
      {isBookingFlow ? bookingTopBar : siteHeader}
      {/* `pb-24` on mobile clears the global StickyMobileBar; on /angebot
          the booking flow's own fixed Weiter-bar handles spacing, so drop it. */}
      <main id="main" className={isBookingFlow ? "pb-0" : "pb-24 md:pb-0"}>
        {children}
      </main>
      {isBookingFlow ? null : footer}
    </>
  );
}
